import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, X, AlertTriangle, List, CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Edit2, DollarSign, Check } from 'lucide-react';
import useSortable from '../hooks/useSortable';
import useDateFilter from '../hooks/useDateFilter';
import SortableHeader from '../components/SortableHeader';
import { LifecycleBadge, EventLegend, offDayFilled } from '../components/LifecycleBadge';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { formatApiError } from '../utils/apiErrors';
import { isStaffActiveOnDate, getStaffWageTypeOnDate, buildStaffDropdownOptions, mergeStaffWithLifecycleHistory } from '../utils/payroll';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import { UiViewToggle } from '../components/ui/UiTabs';
import UiDateField from '../components/ui/UiDateField';
import UiPanel from '../components/ui/UiPanel';
import UiSearchField from '../components/ui/UiSearchField';
import UiTimeField from '../components/ui/UiTimeField';
import { buildStandardPresetOptions, shiftDateRangeByPreset } from '../utils/dateRangeUi';
import { formatUiCurrencyCompact, formatUiDate as formatDate, formatUiDateTimeRange } from '../utils/uiFormat';

const VIOLATION_CHOICES = [
  { value: 'absentee', label: 'Absentee' },
  { value: 'leave', label: 'Leave' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'late_arrival', label: 'Late Arrival' },
  { value: 'early_departure', label: 'Early Departure' },
  { value: 'mobile_usage', label: 'Mobile Phone Usage' },
  { value: 'uniform', label: 'Uniform Violation' },
  { value: 'gloves', label: 'Gloves Usage' },
  { value: 'customer_service', label: 'Customer Service Issue' },
  { value: 'work_quality', label: 'Quality of Work' },
  { value: 'other', label: 'Other' },
];
const FILTER_VIOLATION_CHOICES = VIOLATION_CHOICES;

const VIOLATION_COLORS = {
  absentee: '#6366f1',
  late_arrival: '#f97316',
  early_departure: '#eab308',
  mobile_usage: '#8b5cf6',
  uniform: '#3b82f6',
  gloves: '#06b6d4',
  customer_service: '#ef4444',
  work_quality: '#ec4899',
  other: '#6b7280',
};

const formatDateValueLocal = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const addCalendarDays = (dateStr, days) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return formatDateValueLocal(date);
};
const buildSelectedRangeLabel = (fromDate, fromTime, toDate, toTime) => (
  fromDate && toDate ? formatUiDateTimeRange(fromDate, fromTime, toDate, toTime) : ''
);
const formatCalendarDeductionAmount = (value) => {
  const compact = formatUiCurrencyCompact(Math.abs(Number(value) || 0)).replace(/^Rs\.\s*/, '');
  return `-${compact}`;
};

const SEVERITY_CHOICES = [
  { value: 1, label: 'Minor', color: 'bg-yellow-100 text-yellow-700' },
  { value: 3, label: 'Major', color: 'bg-red-100 text-red-700' },
  { value: 4, label: 'Critical', color: 'bg-stone-800 text-stone-50' },
];

const SUMMARY_BUCKETS = [
  { key: 'absentee', label: 'Absentee', color: 'bg-indigo-100 text-indigo-700', dotClass: 'bg-indigo-400', chipClass: 'bg-indigo-100 text-indigo-700', short: 'Ab' },
  { key: 'minor', label: 'Minor', color: 'bg-yellow-100 text-yellow-700', dotClass: 'bg-yellow-400', chipClass: 'bg-yellow-100 text-yellow-700', short: 'Mi' },
  { key: 'major', label: 'Major', color: 'bg-red-100 text-red-700', dotClass: 'bg-red-400', chipClass: 'bg-red-100 text-red-700', short: 'Ma' },
  { key: 'critical', label: 'Critical', color: 'bg-stone-800 text-stone-50', dotClass: 'bg-stone-800', chipClass: 'bg-stone-800 text-stone-50', short: 'Cr' },
];
const BUCKET_PRIORITY = { minor: 1, absentee: 2, major: 3, critical: 4 };

const initialForm = {
  staff: '',
  violation_type: 'late_arrival',
  severity: 1,
  is_warning: false,
  description: '',
  date: new Date().toISOString().split('T')[0],
};

const isAbsenteeRecord = (record) => {
  return record?.source === 'leave' || record?.violation_type === 'absentee';
};

const getRecordBucketKey = (record) => {
  if (isAbsenteeRecord(record)) return 'absentee';
  const severity = Number(record?.severity || 1);
  if (severity >= 4) return 'critical';
  if (severity >= 2) return 'major';
  return 'minor';
};

const getSummaryBucket = (key) => SUMMARY_BUCKETS.find((bucket) => bucket.key === key) || SUMMARY_BUCKETS[0];
const getHighestBucketKey = (records = []) => records.reduce((current, record) => {
  const next = getRecordBucketKey(record);
  return (BUCKET_PRIORITY[next] || 0) > (BUCKET_PRIORITY[current] || 0) ? next : current;
}, '');
const getBucketCardTone = (bucketKey) => {
  if (bucketKey === 'critical') return { bg: 'bg-stone-100 border-stone-500', txt: 'text-stone-900' };
  if (bucketKey === 'major') return { bg: 'bg-red-50 border-red-300', txt: 'text-red-700' };
  if (bucketKey === 'absentee') return { bg: 'bg-indigo-50 border-indigo-200', txt: 'text-indigo-700' };
  if (bucketKey === 'minor') return { bg: 'bg-yellow-50 border-yellow-200', txt: 'text-yellow-700' };
  return { bg: 'bg-gray-50 border-gray-100', txt: 'text-gray-400' };
};

export default function StaffPerformance({
  addViolationTrigger = 0,
  penaltyTrigger = 0,
  onPayrollChanged = null,
  staffFilter: externalStaffFilter = '',
  onStaffFilterChange = null,
  focusRecord = null,
}) {
  const { hasPermission } = useAuthStore();
  const [records, setRecords] = useState([]);
  const [rawStaff, setRawStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterViolation, setFilterViolation] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [pendingViolations, setPendingViolations] = useState([]);
  const [penaltyRules, setPenaltyRules] = useState([]);
  const [collapsedSummaryStaff, setCollapsedSummaryStaff] = useState(new Set());
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [lifecycleEvents, setLifecycleEvents] = useState([]);
  // Staff MUST carry lifecycle-derived service periods so the dropdown engine sees every service
  // spell (e.g. Arslan: employed→terminated→re-employed), not just the latest hire_date.
  const staff = useMemo(() => mergeStaffWithLifecycleHistory(rawStaff, lifecycleEvents), [rawStaff, lifecycleEvents]);
  const [businessDayStartHour, setBusinessDayStartHour] = useState(6);
  const [viewMode, setViewMode] = useState('table');
  const [focusedId, setFocusedId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const {
    preset: datePreset,
    setPreset: setDatePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    shiftRange,
    applyPreset,
  } = useDateFilter('month', businessDayStartHour);
  const filterEndCalendarDate = endDate ? addCalendarDays(endDate, 1) : '';
  const selectedRangeLabel = useMemo(
    () => buildSelectedRangeLabel(startDate, startTime, filterEndCalendarDate, endTime),
    [startDate, startTime, filterEndCalendarDate, endTime]
  );
  const { sortKey: pSortKey, sortDir: pSortDir, handleSort: pHandleSort, sortData: pSortData } = useSortable('date', 'desc');
  const usesExternalStaffFilter = typeof onStaffFilterChange === 'function';
  const effectiveStaffFilter = usesExternalStaffFilter ? externalStaffFilter : filterStaff;

  const getPenaltyAmount = (severity) => {
    const rule = penaltyRules.find(r => Number(r.severity) === Number(severity));
    const amount = parseFloat(rule?.amount || 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const notifyPayrollChanged = async () => {
    if (typeof onPayrollChanged !== 'function') return;
    await Promise.resolve(onPayrollChanged());
  };

  const selectedPenaltyAmount = useMemo(() => {
    // Holiday never deducts; absentee/leave deduct one day's salary (matching the backend path).
    if (form.violation_type === 'holiday') return 0;
    if (form.violation_type === 'absentee' || form.violation_type === 'leave') {
      const member = staff.find((item) => String(item.id) === String(form.staff));
      const monthlySalary = parseFloat(member?.monthly_salary || 0);
      return Number.isFinite(monthlySalary) && monthlySalary > 0 ? monthlySalary / 30 : 0;
    }
    return getPenaltyAmount(form.severity);
  }, [form.violation_type, form.severity, form.staff, staff, penaltyRules]);

  const getStaffLabel = (staffId) => {
    const member = staff.find(s => String(s.id) === String(staffId));
    return member?.full_name || `${member?.first_name || ''} ${member?.last_name || ''}`.trim();
  };

  const getViolationLabel = (value) => VIOLATION_CHOICES.find(v => v.value === value)?.label || value;

  useEffect(() => {
    api.get('/settings/').then(res => setBusinessDayStartHour(res.data?.business_day_start_hour ?? 6)).catch(() => {});
    api.get('/staff/').then(res => setRawStaff(res.data.results || res.data)).catch(() => {});
    api.get('/public-holidays/?page_size=500').then(res => setPublicHolidays(res.data.results || res.data || [])).catch(() => {});
    api.get('/staff-lifecycle-events/?page_size=2000').then(res => setLifecycleEvents(res.data.results || res.data || [])).catch(() => {});
    fetchPenaltyRules();
  }, []);

  // Employment / re-employment / termination events keyed by `${staff}_${YYYY-MM-DD}`.
  const lifecycleMap = useMemo(() => {
    const m = {};
    (lifecycleEvents || []).forEach(e => {
      if (!['employed', 'reemployed', 'terminated'].includes(e.event_type)) return;
      const d = String(e.event_date || '').substring(0, 10);
      if (!d) return;
      (m[`${e.staff}_${d}`] = m[`${e.staff}_${d}`] || []).push(e);
    });
    return m;
  }, [lifecycleEvents]);

  // When asked to focus a specific record (e.g. from a salary deduction row), switch to the
  // table view and widen the date range to the record's date so it's guaranteed visible,
  // then highlight it briefly.
  useEffect(() => {
    if (!focusRecord?.id) return;
    setViewMode('table');
    setSearch('');
    setFilterViolation('');
    setFilterSeverity('');
    if (focusRecord.date) { setStartDate(focusRecord.date); setEndDate(focusRecord.date); }
    setFocusedId(focusRecord.id);
    const el = () => document.getElementById(`perf-row-${focusRecord.id}`);
    const t = setTimeout(() => { el()?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 250);
    const clear = setTimeout(() => setFocusedId(null), 4000);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [focusRecord?.id, focusRecord?.ts]);

  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        map[formatDateValueLocal(cursor)] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  const fetchPenaltyRules = async () => {
    try {
      const res = await api.get('/staff-penalty-rules/?page_size=20');
      setPenaltyRules(res.data.results || res.data || []);
    } catch (error) {
      toast.error('Failed to load penalty settings');
    }
  };

  const changeCalendarYear = (delta) => {
    const newYear = calendarMonth.year + delta;
    const firstDay = `${newYear}-01-01`;
    const lastDay = `${newYear}-12-31`;
    setCalendarMonth({ year: newYear, month: calendarMonth.month });
    setStartDate(firstDay);
    setEndDate(lastDay);
    setDatePreset('year');
  };

  const changeCalendarMonth = (delta) => {
    let newMonth = calendarMonth.month + delta;
    let newYear = calendarMonth.year;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    else if (newMonth > 11) { newMonth = 0; newYear++; }
    const firstDay = `${newYear}-${String(newMonth+1).padStart(2,'0')}-01`;
    const lastDate = new Date(newYear, newMonth + 1, 0).getDate();
    const lastDay = `${newYear}-${String(newMonth+1).padStart(2,'0')}-${String(lastDate).padStart(2,'0')}`;
    setCalendarMonth({ year: newYear, month: newMonth });
    setStartDate(firstDay);
    setEndDate(lastDay);
    setDatePreset('month');
  };

  useEffect(() => {
    if (viewMode !== 'calendar' || ['month', 'year'].includes(datePreset)) return;
    const base = startDate ? new Date(`${startDate}T12:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const firstDay = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDate = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const lastDay = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(lastDate).padStart(2, '0')}`;
    setCalendarMonth({ year: base.getFullYear(), month: base.getMonth() });
    setStartDate(firstDay);
    setEndDate(lastDay);
    setDatePreset('month');
  }, [viewMode, datePreset, startDate]);

  const applyPerformanceDatePreset = (preset) => {
    if (viewMode === 'calendar' && ['month', 'year'].includes(preset)) {
      const base = new Date(calendarMonth.year, calendarMonth.month, 1);
      const firstDay = preset === 'year'
        ? `${base.getFullYear()}-01-01`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-01`;
      const lastDay = preset === 'year'
        ? `${base.getFullYear()}-12-31`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      setStartDate(firstDay);
      setEndDate(lastDay);
      setCalendarMonth({ year: base.getFullYear(), month: preset === 'year' ? 0 : base.getMonth() });
      setDatePreset(preset);
      return;
    }
    setDatePreset(preset);
    applyPreset(preset);
  };

  const handlePerformanceCalendarPeriodChange = ({ mode, startDate: periodStart, endDate: periodEnd, year, month }) => {
    setStartDate(periodStart);
    setEndDate(periodEnd);
    setCalendarMonth({ year, month: mode === 'year' ? 0 : month });
    setDatePreset(mode);
  };

  const shiftPerformanceDateRange = (delta) => {
    if (viewMode !== 'calendar') {
      shiftRange(delta);
      return;
    }
    const shifted = shiftDateRangeByPreset({
      preset: datePreset,
      startDate,
      endDate,
      delta,
    });
    const base = new Date(`${shifted.startDate}T12:00:00`);
    if (Number.isNaN(base.getTime())) return;
    handlePerformanceCalendarPeriodChange({
      mode: datePreset === 'year' ? 'year' : 'month',
      startDate: shifted.startDate,
      endDate: shifted.endDate,
      year: base.getFullYear(),
      month: datePreset === 'year' ? 0 : base.getMonth(),
    });
  };

  const handlePerformanceStartDateChange = (value) => {
    setStartDate(value);
  };

  const handlePerformanceEndDateChange = (value) => {
    setEndDate(addCalendarDays(value, -1));
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // Update calendar view when custom date changes
  useEffect(() => {
    if (startDate && viewMode === 'calendar') {
      const d = new Date(startDate);
      if (!isNaN(d.getTime())) {
        setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
      }
    }
  }, [startDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [perfRes, leaveReviewRes] = await Promise.all([
        api.get(`/staff-performance/?start_date=${startDate}&end_date=${endDate}&page_size=2000`),
        api.get(`/staff-leave-violation-reviews/?start_date=${startDate}&end_date=${endDate}&page_size=4000`),
      ]);
      const performanceRecords = perfRes.data.results || perfRes.data || [];
      const leaveReviewRecords = leaveReviewRes.data.results || leaveReviewRes.data || [];
      setRecords([...performanceRecords, ...leaveReviewRecords]);
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  // Keep the table/summary within the selected date range even if a fetch is stale
  // or the server returns extra rows — the range is the source of truth.
  const inSelectedRange = (r) => !startDate || !endDate || (r.date && r.date >= startDate && r.date <= endDate);

  const filtered = useMemo(() => {
    let src = records.filter(inSelectedRange);
    if (effectiveStaffFilter) src = src.filter(r => String(r.staff) === String(effectiveStaffFilter));
    if (filterViolation) src = src.filter(r => r.violation_type === filterViolation);
    if (filterSeverity) {
      src = src.filter((record) => {
        if (filterSeverity === 'absentee') return isAbsenteeRecord(record);
        if (filterSeverity === '3') return !isAbsenteeRecord(record) && Number(record.severity || 1) >= 2 && Number(record.severity || 1) < 4;
        if (filterSeverity === '4') return !isAbsenteeRecord(record) && Number(record.severity || 1) >= 4;
        return !isAbsenteeRecord(record) && Number(record.severity || 1) === Number(filterSeverity);
      });
    }
    if (search) {
      const q = search.toLowerCase();
      src = src.filter(r => `${r.staff_name} ${r.violation_type_display} ${r.description}`.toLowerCase().includes(q));
    }
    return pSortData(src);
  }, [records, startDate, endDate, effectiveStaffFilter, filterViolation, filterSeverity, search, pSortKey, pSortDir]);

  const summaryFilteredRecords = useMemo(() => {
    let src = records.filter(inSelectedRange);
    if (effectiveStaffFilter) src = src.filter(r => String(r.staff) === String(effectiveStaffFilter));
    if (filterViolation) src = src.filter(r => r.violation_type === filterViolation);
    if (filterSeverity) {
      src = src.filter((record) => {
        if (filterSeverity === 'absentee') return isAbsenteeRecord(record);
        if (filterSeverity === '3') return !isAbsenteeRecord(record) && Number(record.severity || 1) >= 2 && Number(record.severity || 1) < 4;
        if (filterSeverity === '4') return !isAbsenteeRecord(record) && Number(record.severity || 1) >= 4;
        return !isAbsenteeRecord(record) && Number(record.severity || 1) === Number(filterSeverity);
      });
    }
    return src;
  }, [records, startDate, endDate, effectiveStaffFilter, filterViolation, filterSeverity]);

  const performanceSummaries = useMemo(() => {
    const createBucket = () => ({
      count: 0,
      deducted: 0,
      waived: 0,
    });
    const createBucketMap = () => SUMMARY_BUCKETS.reduce((acc, bucket) => {
      acc[bucket.key] = createBucket();
      return acc;
    }, {});
    const byStaff = {};
    const grand = {
      totalCount: 0,
      totalDeducted: 0,
      totalWaived: 0,
      buckets: createBucketMap(),
    };

    summaryFilteredRecords.forEach((record) => {
      const staffId = String(record.staff || '');
      const bucketKey = getRecordBucketKey(record);
      const amount = parseFloat(record.penalty_amount || 0) || 0;
      if (!byStaff[staffId]) {
        byStaff[staffId] = {
          staffId,
          staffName: record.staff_name || getStaffLabel(record.staff) || 'Unknown Staff',
          totalCount: 0,
          totalDeducted: 0,
          totalWaived: 0,
          buckets: createBucketMap(),
        };
      }
      const target = byStaff[staffId];
      const summaryBucket = target.buckets[bucketKey] || target.buckets.minor;
      const grandBucket = grand.buckets[bucketKey] || grand.buckets.minor;

      target.totalCount += 1;
      grand.totalCount += 1;
      summaryBucket.count += 1;
      grandBucket.count += 1;

      if (record.is_warning) {
        target.totalWaived += amount;
        grand.totalWaived += amount;
        summaryBucket.waived += amount;
        grandBucket.waived += amount;
      } else {
        target.totalDeducted += amount;
        grand.totalDeducted += amount;
        summaryBucket.deducted += amount;
        grandBucket.deducted += amount;
      }
    });

    const rows = Object.values(byStaff).sort((a, b) => (
      b.totalDeducted - a.totalDeducted
      || b.totalCount - a.totalCount
      || a.staffName.localeCompare(b.staffName)
    ));

    return { rows, grand };
  }, [summaryFilteredRecords, staff]);

  useEffect(() => {
    setCollapsedSummaryStaff(new Set(performanceSummaries.rows.map((row) => row.staffId)));
  }, [performanceSummaries.rows]);

  const toggleSummaryStaff = (staffId) => {
    setCollapsedSummaryStaff((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) {
      handleApplyViolation();
      return;
    }
    try {
      if (editingItem?.source === 'leave') {
        await api.patch(`/staff-leave-violation-reviews/${editingItem.id}/`, { is_warning: form.is_warning });
        toast.success('Leave violation updated');
      } else if (editingItem?.id) {
        await api.patch(`/staff-performance/${editingItem.id}/`, form);
        toast.success('Performance record updated');
      }
      setShowModal(false);
      setForm(initialForm);
      setEditingItem(null);
      fetchData();
      await notifyPayrollChanged();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save performance record'));
    }
  };

  const validateViolationForm = () => {
    if (!form.staff) {
      toast.error('Select staff');
      return false;
    }
    if (!form.date) {
      toast.error('Select date');
      return false;
    }
    return true;
  };

  const handleApplyViolation = () => {
    if (!validateViolationForm()) return;
    const pending = {
      ...form,
      _key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      staff_name: getStaffLabel(form.staff),
      violation_type_display: getViolationLabel(form.violation_type),
      severity_display: form.violation_type === 'absentee' ? 'Absentee' : getSeverityInfo(form.severity).label,
      penalty_amount: selectedPenaltyAmount,
    };
    setPendingViolations(prev => [...prev, pending]);
  };

  const handleSavePendingViolations = async () => {
    if (pendingViolations.length === 0) {
      toast.error('Apply at least one violation before saving');
      return;
    }
    try {
      // One atomic request — all violations save together or none do.
      await api.post('/staff-performance/bulk-save/', {
        entries: pendingViolations.map(item => ({
          staff: item.staff,
          violation_type: item.violation_type,
          severity: item.severity,
          is_warning: item.is_warning,
          description: item.description,
          date: item.date,
        })),
      });
      toast.success(`${pendingViolations.length} violation${pendingViolations.length !== 1 ? 's' : ''} saved`);
      setShowModal(false);
      setForm(initialForm);
      setPendingViolations([]);
      fetchData();
      await notifyPayrollChanged();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save performance records'));
    }
  };

  const handleToggleWarning = async (item) => {
    try {
      if (item.source === 'leave') {
        await api.patch(`/staff-leave-violation-reviews/${item.id}/`, { is_warning: !item.is_warning });
      } else {
        await api.patch(`/staff-performance/${item.id}/`, { is_warning: !item.is_warning });
      }
      toast.success(!item.is_warning ? 'Penalty waived as warning' : 'Penalty restored');
      fetchData();
      await notifyPayrollChanged();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update warning status'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this performance record?')) return;
    try {
      const target = records.find((record) => String(record.id) === String(id));
      if (target?.source === 'leave') {
        await api.delete(`/staff-leave-violation-reviews/${id}/`);
        toast.success('Leave violation cleared');
      } else {
        await api.delete(`/staff-performance/${id}/`);
        toast.success('Record deleted');
      }
      fetchData();
      await notifyPayrollChanged();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete'));
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setForm(initialForm);
    setPendingViolations([]);
    setShowModal(true);
  };

  const openEdit = (item) => {
    if (item?.source === 'leave') {
      setEditingItem(item);
      setForm({
        staff: item.staff,
        violation_type: 'other',
        severity: 1,
        is_warning: !!item.is_warning,
        description: item.description || '',
        date: item.date,
      });
      setShowModal(true);
      return;
    }
    const normalizedSeverity = Number(item.severity || 1) >= 4 ? 4 : Number(item.severity || 1) >= 2 ? 3 : 1;
    setEditingItem(item);
    setForm({ staff: item.staff, violation_type: item.violation_type || 'other', severity: normalizedSeverity, is_warning: !!item.is_warning, description: item.description, date: item.date });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setForm(initialForm);
    setPendingViolations([]);
  };

  useEffect(() => { if (addViolationTrigger > 0) openCreate(); }, [addViolationTrigger]);
  useEffect(() => { if (penaltyTrigger > 0) setShowPenaltyModal(true); }, [penaltyTrigger]);

  // Filter dropdown — staff in service during the viewed date range (terminated shown if in-range).
  const staffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { fromDate: startDate, toDate: endDate, keepValue: effectiveStaffFilter }),
    [staff, startDate, endDate, effectiveStaffFilter]);
  // Add/Edit modal — staff active on the chosen record date.
  const datedStaffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { onDate: form.date, keepValue: form.staff }),
    [staff, form.date, form.staff]);

  const getSeverityInfo = (val, record = null) => {
    if (isAbsenteeRecord(record)) return getSummaryBucket('absentee');
    if (Number(val) >= 4) return getSummaryBucket('critical');
    if (Number(val) >= 2) return getSummaryBucket('major');
    return getSummaryBucket('minor');
  };

  const getPenaltyRule = (severity) => penaltyRules.find(rule => Number(rule.severity) === Number(severity));

  const setPenaltyRuleAmount = (severity, amount) => {
    setPenaltyRules(prev => {
      const existing = prev.find(rule => Number(rule.severity) === Number(severity));
      if (existing) {
        return prev.map(rule => Number(rule.severity) === Number(severity) ? { ...rule, amount } : rule);
      }
      return [...prev, { severity, amount }];
    });
  };

  const handleSavePenaltyRules = async () => {
    try {
      await Promise.all(SEVERITY_CHOICES.map(severity => {
        const rule = getPenaltyRule(severity.value);
        const payload = { severity: severity.value, amount: parseFloat(rule?.amount || 0) || 0 };
        return rule?.id
          ? api.patch(`/staff-penalty-rules/${rule.id}/`, payload)
          : api.post('/staff-penalty-rules/', payload);
      }));
      toast.success('Penalty settings saved');
      setShowPenaltyModal(false);
      fetchPenaltyRules();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save penalty settings'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;
  }

  return (
    <div className="space-y-4">
          <UiDateRangeBlock
            preset={datePreset}
            presetOptions={viewMode === 'calendar'
              ? buildStandardPresetOptions(['month', 'year'])
              : buildStandardPresetOptions(['today', 'week', 'month', 'year', 'custom'])}
            onPresetChange={applyPerformanceDatePreset}
            onPrevious={() => shiftPerformanceDateRange(-1)}
            onNext={() => shiftPerformanceDateRange(1)}
            periodPicker={viewMode === 'calendar'}
            onPeriodChange={handlePerformanceCalendarPeriodChange}
            fromDate={startDate}
            onFromDateChange={handlePerformanceStartDateChange}
            toDate={filterEndCalendarDate}
            onToDateChange={handlePerformanceEndDateChange}
            fromTime={startTime}
            onFromTimeChange={setStartTime}
            toTime={endTime}
            onToTimeChange={setEndTime}
            rangeText={selectedRangeLabel ? `Selected range: ${selectedRangeLabel}` : ''}
            header={(
              <div className="flex flex-wrap items-center gap-2">
              <UiViewToggle
                options={[{ value: 'table', label: 'Table' }, { value: 'calendar', label: 'Calendar' }]}
                value={viewMode}
                onChange={setViewMode}
              />
              <span className="ml-auto text-xs text-gray-400">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={effectiveStaffFilter}
                onChange={e => {
                  const nextValue = e.target.value;
                  if (usesExternalStaffFilter) onStaffFilterChange(nextValue);
                  else setFilterStaff(nextValue);
                }}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">All Staff</option>
                {staffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={filterViolation} onChange={e => setFilterViolation(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="">All Violations</option>
                {FILTER_VIOLATION_CHOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
              <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="">All Severity</option>
                {SUMMARY_BUCKETS.map(bucket => <option key={bucket.key} value={bucket.key === 'absentee' ? 'absentee' : bucket.key === 'critical' ? '4' : bucket.key === 'major' ? '3' : '1'}>{bucket.label}</option>)}
              </select>
            </div>
          </UiDateRangeBlock>

          <UiPanel bodyClassName="p-4">
            <UiSearchField value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by staff, violation, notes..." />
          </UiPanel>

          <UiPanel bodyClassName="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Performance Summary by Staff</h3>
                <p className="text-xs text-gray-500">Counts and salary deductions for the selected date range and filters.</p>
              </div>
              <div className="text-xs text-gray-500">
                {performanceSummaries.rows.length} staff
              </div>
            </div>

            <div className="rounded-xl border border-primary-100 bg-primary-50/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-primary-600 font-semibold">Grand Total</p>
                  <p className="text-sm font-semibold text-gray-800">{performanceSummaries.grand.totalCount} record{performanceSummaries.grand.totalCount !== 1 ? 's' : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Salary Deducted</p>
                  <p className="text-sm font-bold text-red-600">{formatUiCurrencyCompact(performanceSummaries.grand.totalDeducted)}</p>
                  <p className="text-xs text-gray-500">Waived: {formatUiCurrencyCompact(performanceSummaries.grand.totalWaived)}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {SUMMARY_BUCKETS.map((bucketMeta) => {
                  const bucket = performanceSummaries.grand.buckets[bucketMeta.key];
                  return (
                    <div key={`grand-${bucketMeta.key}`} className="rounded-lg border border-white/70 bg-white/70 p-2">
                      <p className="text-xs font-semibold text-gray-700">{bucketMeta.label}</p>
                      <p className="text-sm font-bold text-gray-900">{bucket.count}</p>
                      <p className="text-[11px] text-red-600">Deducted: {formatUiCurrencyCompact(bucket.deducted)}</p>
                      <p className="text-[11px] text-gray-500">Waived: {formatUiCurrencyCompact(bucket.waived)}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              {performanceSummaries.rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">No performance records for this selection.</div>
              ) : performanceSummaries.rows.map((staffSummary) => (
                <div key={staffSummary.staffId} className="border border-gray-100 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleSummaryStaff(staffSummary.staffId)}
                    className="w-full px-3 py-3 bg-gray-50 hover:bg-gray-100 grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3 text-left transition"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {collapsedSummaryStaff.has(staffSummary.staffId) ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800 truncate">{staffSummary.staffName}</span>
                        <span className="block text-xs text-gray-500">{staffSummary.totalCount} record{staffSummary.totalCount !== 1 ? 's' : ''}</span>
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-xs text-gray-500">Salary Deducted</span>
                      <span className="block text-sm font-bold text-red-600">{formatUiCurrencyCompact(staffSummary.totalDeducted)}</span>
                      <span className="block text-[11px] text-gray-500">Waived: {formatUiCurrencyCompact(staffSummary.totalWaived)}</span>
                    </span>
                  </button>
                  {!collapsedSummaryStaff.has(staffSummary.staffId) && (
                    <div className="divide-y divide-gray-100 bg-white">
                      {SUMMARY_BUCKETS.map((bucketMeta) => {
                        const bucket = staffSummary.buckets[bucketMeta.key];
                        return (
                          <div key={`${staffSummary.staffId}-${bucketMeta.key}`} className="px-9 py-2 grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3 text-left">
                            <span className="text-xs sm:text-sm text-gray-600">{bucketMeta.label} ({bucket.count})</span>
                            <span className="text-right">
                              <span className="block text-xs font-semibold text-red-600">Deducted: {formatUiCurrencyCompact(bucket.deducted)}</span>
                              <span className="block text-[11px] text-gray-500">Waived: {formatUiCurrencyCompact(bucket.waived)}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </UiPanel>

          {/* Calendar View */}
          {viewMode === 'calendar' && (() => {
            const MO_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const MO_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const todayStr = new Date().toISOString().substring(0, 10);
            const calYr = calendarMonth.year;
            
            /* ── Year Grid View ── */
            if (datePreset === 'year') {
              const monthAgg = {};
              for (let m = 0; m < 12; m++) {
                const ms = `${calYr}-${String(m+1).padStart(2,'0')}`;
                const monthViolations = filtered.filter(r => r.date.substring(0, 7) === ms);
                const bucketCounts = monthViolations.reduce((acc, r) => {
                  const bucketKey = getRecordBucketKey(r);
                  acc[bucketKey] = (acc[bucketKey] || 0) + 1;
                  return acc;
                }, {});
                const deductedTotal = monthViolations.reduce(
                  (sum, record) => sum + (!record.is_warning ? (parseFloat(record.penalty_amount || 0) || 0) : 0),
                  0
                );
                monthAgg[ms] = {
                  count: monthViolations.length,
                  byBucket: bucketCounts,
                  records: monthViolations,
                  deductedTotal,
                };
              }
              return (
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => changeCalendarYear(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><ChevronLeft size={18} /></button>
                    <h3 className="font-semibold text-gray-800">{calYr} — Violations Overview</h3>
                    <button onClick={() => changeCalendarYear(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><ChevronRight size={18} /></button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {MO_SHORT.map((mName, mi) => {
                      const ms = `${calYr}-${String(mi+1).padStart(2,'0')}`;
                      const agg = monthAgg[ms] || { count: 0, byBucket: {} };
                      const isFutureMonth = ms > todayStr.substring(0,7);
                      let bg = 'bg-gray-50 border-gray-200', txt = 'text-gray-400';
                      if (!isFutureMonth && agg.count > 0) {
                        const tone = getBucketCardTone(getHighestBucketKey(agg.records || []));
                        bg = tone.bg;
                        txt = tone.txt;
                      } else if (!isFutureMonth) {
                        bg = 'bg-gray-300 border-gray-400';
                        txt = 'text-gray-600';
                      }
                      return (
                        <div key={ms} onClick={() => {
                          if (!isFutureMonth && agg.count > 0) {
                            const firstDay = `${calYr}-${String(mi + 1).padStart(2, '0')}-01`;
                            const lastDay = `${calYr}-${String(mi + 1).padStart(2, '0')}-${String(new Date(calYr, mi + 1, 0).getDate()).padStart(2, '0')}`;
                            setStartDate(firstDay);
                            setEndDate(lastDay);
                            setDatePreset('month');
                            setCalendarMonth({ year: calYr, month: mi });
                          }
                        }}
                          className={`${bg} border rounded-xl p-3 ${!isFutureMonth && agg.count > 0 ? 'cursor-pointer hover:shadow-md transition' : 'opacity-40'}`}>
                          <div className={`font-bold text-sm ${txt}`}>{mName}</div>
                          {!isFutureMonth && agg.count > 0 && (
                            <div className="mt-2 space-y-1">
                              <div className="flex flex-wrap gap-1">
                                {(agg.records || []).slice(0, 24).map((record, dotIndex) => {
                                  const bucket = getSummaryBucket(getRecordBucketKey(record));
                                  return <span key={`${ms}-dot-${record.id || dotIndex}`} className={`w-1.5 h-1.5 rounded-full ${bucket.dotClass}`} />;
                                })}
                              </div>
                              {agg.deductedTotal > 0 && (
                                <div className="text-xs font-bold text-red-700">{formatCalendarDeductionAmount(agg.deductedTotal)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t text-xs text-gray-500">
                    {SUMMARY_BUCKETS.map((bucket) => (
                      <span key={`year-legend-${bucket.key}`} className="flex items-center gap-1"><span className={`w-2 h-2 rounded ${bucket.dotClass}`}/> {bucket.label}</span>
                    ))}
                  </div>
                </div>
              );
            }
            
            /* ── Month Calendar View ── */
            const firstDay = (new Date(calendarMonth.year, calendarMonth.month, 1).getDay() + 6) % 7;
            const daysInMonth = new Date(calendarMonth.year, calendarMonth.month + 1, 0).getDate();
            const dayMap = {};
            filtered.forEach(r => {
              const [y, m, d] = r.date.split('-').map(Number);
              if (y === calendarMonth.year && m === calendarMonth.month + 1) {
                if (!dayMap[d]) dayMap[d] = [];
                dayMap[d].push(r);
              }
            });
            const cells = [];
            for (let i = 0; i < firstDay; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            while (cells.length % 7 !== 0) cells.push(null);
            
            return (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => changeCalendarMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><ChevronLeft size={18} /></button>
                  <h3 className="font-semibold text-gray-800">{MO_NAMES[calendarMonth.month]} {calendarMonth.year}</h3>
                  <button onClick={() => changeCalendarMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"><ChevronRight size={18} /></button>
                </div>
                <div className="grid grid-cols-7 mb-1 text-center">
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(dn => <div key={dn} className="text-[11px] font-medium text-gray-400 py-1">{dn}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((day, idx) => {
                    if (!day) return <div key={idx} className="min-h-[4.5rem] rounded-lg bg-gray-50/60" />;
                    const dayRecords = dayMap[day] || [];
                    const dateStr = `${calendarMonth.year}-${String(calendarMonth.month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const holiday = holidayMap[dateStr];
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const dayDeductedTotal = dayRecords.reduce(
                      (sum, record) => sum + (!record.is_warning ? (parseFloat(record.penalty_amount || 0) || 0) : 0),
                      0
                    );
                    let bg = isPast ? 'bg-gray-300 border-gray-400' : 'bg-gray-50 border-gray-100', txt = isPast ? 'text-gray-600' : 'text-gray-400';
                    if (dayRecords.length > 0) {
                      const tone = getBucketCardTone(getHighestBucketKey(dayRecords));
                      bg = tone.bg;
                      txt = tone.txt;
                    } else if (holiday) {
                      bg = 'bg-amber-50 border-amber-300';
                      txt = 'text-amber-700';
                    }
                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          if (dayRecords.length > 0) {
                            setStartDate(dateStr);
                            setEndDate(dateStr);
                            setDatePreset('custom');
                            setViewMode('table');
                          }
                        }}
                        className={`min-h-[4.5rem] p-1.5 rounded-lg border ${holiday ? 'ring-1 ring-amber-300 ' : ''}${isToday ? 'ring-2 ring-primary-500 ' : ''}${bg} ${dayRecords.length > 0 ? 'cursor-pointer hover:shadow-sm' : ''}`}
                      >
                        <div className={`text-xs font-bold ${isToday ? 'text-primary-600' : txt}`}>{day}</div>
                        {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                        {dayRecords.filter(r => ['leave', 'holiday', 'absentee'].includes(r.violation_type)).map((r, i) => (
                          <LifecycleBadge key={`off-${i}`} type={r.violation_type}
                            name={effectiveStaffFilter ? ({ leave: 'Leave', holiday: 'Holiday', absentee: 'Absentee' }[r.violation_type]) : (r.staff_name || '').split(' ')[0]}
                            filled={offDayFilled(r.violation_type, r.is_warning)} size={13} />
                        ))}
                        {(() => {
                          const evs = Object.keys(lifecycleMap)
                            .filter(k => k.endsWith(`_${dateStr}`) && (!effectiveStaffFilter || k.startsWith(`${effectiveStaffFilter}_`)))
                            .flatMap(k => lifecycleMap[k]);
                          return evs.map((ev, i) => (
                            <LifecycleBadge key={`life-${i}`} type={ev.event_type} name={(ev.staff_name || '').split(' ')[0]} size={13} />
                          ));
                        })()}
                        {(() => {
                          // Off-days render as icons above. Of the remaining violations: those with an
                          // actual deduction show as a left-aligned line "• name (-amount)" (like the
                          // day-off rows); those with no financial penalty stay as compact dots on top.
                          const violations = dayRecords.filter(r => !['leave', 'holiday', 'absentee'].includes(r.violation_type));
                          const deducting = violations.filter(r => !r.is_warning && Number(r.penalty_amount || 0) > 0);
                          const nonDeducting = violations.filter(r => r.is_warning || Number(r.penalty_amount || 0) <= 0);
                          if (violations.length === 0) return null;
                          return (
                            <div className="mt-1 space-y-0.5">
                              {nonDeducting.length > 0 && (
                                <div className="flex flex-wrap gap-0.5">
                                  {nonDeducting.slice(0, 18).map((record, dotIndex) => {
                                    const bucket = getSummaryBucket(getRecordBucketKey(record));
                                    return <span key={`${dateStr}-dot-${record.id || dotIndex}`} className={`w-1.5 h-1.5 rounded-full ${bucket.dotClass}`} title={record.violation_type_display || record.violation_type} />;
                                  })}
                                </div>
                              )}
                              {deducting.slice(0, 8).map((record, i) => {
                                const bucket = getSummaryBucket(getRecordBucketKey(record));
                                const label = effectiveStaffFilter ? (record.violation_type_display || record.violation_type) : (record.staff_name || '').split(' ')[0];
                                return (
                                  <div key={`${dateStr}-d-${record.id || i}`} className="flex items-center gap-1 text-[9px] leading-tight">
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${bucket.dotClass}`} />
                                    <span className="truncate text-gray-600">{label}</span>
                                    <span className="text-red-700 font-semibold shrink-0">({formatCalendarDeductionAmount(-Number(record.penalty_amount || 0))})</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-stone-800"/> Critical</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"/> Major</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-400"/> Absentee</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"/> Minor</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300"/> Holiday</span>
                  <EventLegend />
                </div>
              </div>
            );
          })()}

          {/* Table View */}
          {viewMode === 'table' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <SortableHeader label="Staff" col="staff_name" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Violation" col="violation_type" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Severity" col="severity" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Date" col="date" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Penalty" col="penalty_amount" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Warning" col="is_warning" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Notes" col="description" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <SortableHeader label="Logged By" col="logged_by_name" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(item => {
                  const sev = getSeverityInfo(item.severity, item);
                  return (
                    <tr key={item.id} id={`perf-row-${item.id}`} className={`hover:bg-gray-50 ${focusedId === item.id ? 'bg-amber-100 ring-2 ring-inset ring-amber-400' : ''} ${hasPermission('performance', 'edit') ? 'cursor-pointer' : ''}`} onClick={() => hasPermission('performance', 'edit') && openEdit(item)}>
                      <td className="px-4 py-3 font-medium">{item.staff_name}<div className="text-xs text-gray-400">{item.staff_code}</div></td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1"><AlertTriangle size={13} className="text-orange-400" />{item.violation_type_display}</div></td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${sev.color}`}>{sev.label}</span></td>
                      <td className="px-4 py-3">{formatDate(item.date)}</td>
                      <td className={`px-4 py-3 font-semibold ${item.is_warning ? 'text-gray-400 line-through' : 'text-red-600'}`}>{formatUiCurrencyCompact(item.penalty_amount)}</td>
                      <td className="px-4 py-3">
                        <label className="inline-flex items-center gap-2 text-xs text-gray-600" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={!!item.is_warning} onChange={() => handleToggleWarning(item)} disabled={!hasPermission('performance', 'edit')} />
                          {item.is_warning ? 'Waived' : 'Deduct'}
                        </label>
                      </td>
                      <td className="px-4 py-3 max-w-xs"><span className="truncate text-sm block">{item.description || '-'}</span></td>
                      <td className="px-4 py-3">{item.logged_by_name || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {hasPermission('performance', 'edit') && (
                            <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={14} /></button>
                          )}
                          {hasPermission('performance', 'delete') && (
                            <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="text-center py-12 text-gray-500">No performance records found</div>}
          </div>
          )}

      {/* Penalty Settings Modal */}
      {showPenaltyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <DollarSign size={18} className="text-red-500" /> Penalty Settings
              </h2>
              <button onClick={() => setShowPenaltyModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {SEVERITY_CHOICES.map(severity => {
                const rule = getPenaltyRule(severity.value);
                return (
                  <div key={severity.value} className="flex items-center justify-between gap-3 border rounded-lg p-3">
                    <div>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${severity.color}`}>{severity.label}</span>
                      <p className="text-xs text-gray-400 mt-1">Used when this severity is selected.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Rs.</span>
                      <input
                        type="number"
                        value={rule?.amount ?? ''}
                        onChange={(e) => setPenaltyRuleAmount(severity.value, e.target.value)}
                        className="w-32 px-3 py-2 border rounded-lg text-right"
                        min="0"
                        step="0.01"
                        placeholder="0"
                      />
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between gap-3 border rounded-lg p-3 bg-indigo-50 border-indigo-200">
                <div>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">Absentee</span>
                  <p className="text-xs text-indigo-700 mt-1">Leave marked as a violation.</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-indigo-800">1 day salary</p>
                  <p className="text-[11px] text-indigo-600">Monthly salary / 30</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowPenaltyModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="button" onClick={handleSavePenaltyRules} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Save Penalty</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">{editingItem?.source === 'leave' ? 'Review Leave Violation' : editingItem ? 'Edit' : 'Log'} Performance Record</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Date leads — it determines who was employed and their wage type on that day. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <UiDateField value={form.date} onChange={value => setForm({ ...form, date: value })} disabled={editingItem?.source === 'leave'} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
                <select value={form.staff} onChange={e => setForm({ ...form, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required disabled={editingItem?.source === 'leave'}>
                  <option value="">Select staff</option>
                  {datedStaffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {form.staff && (() => { const m = staff.find(s => String(s.id) === String(form.staff)); return m ? <p className="text-[11px] text-gray-500 mt-1">On {form.date}: <span className="font-medium">{getStaffWageTypeOnDate(m, form.date) === 'daily_wages' ? 'Daily-wage' : 'Salaried'}</span></p> : null; })()}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Violation Type *</label>
                  <select value={editingItem?.source === 'leave' ? 'absentee' : form.violation_type} onChange={e => setForm({ ...form, violation_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required disabled={editingItem?.source === 'leave'}>
                    {editingItem?.source === 'leave' ? <option value="absentee">Absentee</option> : VIOLATION_CHOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
                  <select value={editingItem?.source === 'leave' ? 1 : form.severity} onChange={e => setForm({ ...form, severity: Number(e.target.value) })} className="w-full px-3 py-2 border rounded-lg" required disabled={editingItem?.source === 'leave'}>
                    {editingItem?.source === 'leave' ? <option value={1}>Absentee</option> : SEVERITY_CHOICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-red-500 font-medium">Penalty Amount</p>
                  <p className="text-lg font-bold text-red-700">{formatUiCurrencyCompact(editingItem?.source === 'leave' ? parseFloat(editingItem?.penalty_amount || 0) : selectedPenaltyAmount)}</p>
                  <p className="text-xs text-red-400">{editingItem?.source === 'leave' ? '1 day salary for this leave date.' : 'Picked from Penalty settings for this severity.'}</p>
                </div>
                <label className="flex items-center gap-3 border rounded-lg px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.is_warning}
                    onChange={e => setForm({ ...form, is_warning: e.target.checked })}
                  />
                  Warning only, waive salary deduction
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description / Notes</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the incident..." className="w-full px-3 py-2 border rounded-lg" rows="3" disabled={editingItem?.source === 'leave'} />
              </div>
              {!editingItem && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pending Violations ({pendingViolations.length})</p>
                    <button type="button" onClick={handleApplyViolation} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1">
                      Apply
                    </button>
                  </div>
                  {pendingViolations.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-56 overflow-auto">
                      {pendingViolations.map((item) => (
                        <div key={item._key} className="p-3 text-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-medium text-gray-800">{item.staff_name} - {item.violation_type_display}</p>
                              <p className="text-xs text-gray-500">{formatDate(item.date)} · {item.severity_display} · {formatUiCurrencyCompact(item.penalty_amount)}</p>
                              {item.description && <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>}
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-gray-500 flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={!!item.is_warning}
                                  onChange={(e) => setPendingViolations(prev => prev.map(row => row._key === item._key ? { ...row, is_warning: e.target.checked } : row))}
                                />
                                Warning
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  setForm({ staff: item.staff, violation_type: item.violation_type, severity: item.severity, is_warning: item.is_warning, description: item.description, date: item.date });
                                  setPendingViolations(prev => prev.filter(row => row._key !== item._key));
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingViolations(prev => prev.filter(row => row._key !== item._key))}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-lg">Cancel</button>
                {editingItem ? (
                  <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
                ) : (
                  <button type="button" onClick={handleSavePendingViolations} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save All</button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
